import { Row, Table as TableInstance } from '@tanstack/react-table';

import { AutomationTestingProps } from '@/types';

import { Table } from './Table';
import { DefaultType } from './types';
import { TableGroupedContent } from './TableGroupedContent';

interface Props<D extends DefaultType> extends AutomationTestingProps {
  tableInstance: TableInstance<D>;
  renderRow(row: Row<D>): React.ReactNode;
  getGroupKey?(row: Row<D>, table: TableInstance<D>): string;
  onSortChange?(colId: string, desc: boolean): void;
  isLoading?: boolean;
  emptyContentLabel?: string;
  'aria-label'?: string;
}

export function DatatableContent<D extends DefaultType>({
  tableInstance,
  renderRow,
  getGroupKey,
  onSortChange,
  isLoading,
  emptyContentLabel,
  'data-cy': dataCy,
  'aria-label': ariaLabel,
}: Props<D>) {
  const headerGroups = tableInstance.getHeaderGroups();
  const pageRowModel = tableInstance.getPaginationRowModel();
  const sortedColumn = tableInstance
    .getAllColumns()
    .find((col) => col.getIsSorted());
  const groupedContent =
    !sortedColumn?.columnDef.meta?.disableGrouping && getGroupKey;

  return (
    <Table data-cy={dataCy} className="nowrap-cells" aria-label={ariaLabel}>
      <thead>
        {headerGroups.map((headerGroup) => (
          <Table.HeaderRow<D>
            key={headerGroup.id}
            headers={headerGroup.headers}
            onSortChange={onSortChange}
            tableMeta={tableInstance.options.meta}
          />
        ))}
      </thead>
      <tbody>
        {groupedContent ? (
          <TableGroupedContent<D>
            rows={pageRowModel.rows}
            isLoading={isLoading}
            emptyContent={emptyContentLabel}
            renderRow={renderRow}
            getGroupKey={(row) => getGroupKey(row, tableInstance)}
            isSelectionEnabled={!!tableInstance.options.enableRowSelection}
          />
        ) : (
          <Table.Content<D>
            rows={pageRowModel.rows}
            isLoading={isLoading}
            emptyContent={emptyContentLabel}
            renderRow={renderRow}
          />
        )}
      </tbody>
    </Table>
  );
}
